
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowUpCircle, ArrowDownCircle, Clock, CheckCircle, XCircle, ExternalLink, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export interface Transaction {
  id: number;
  type: "deposit" | "withdrawal";
  amount: string;
  status: "completed" | "pending" | "failed";
  date: string;
  hash: string;
  verified?: boolean;
}

interface TransactionHistoryProps {
  transactions: Transaction[];
}

export const TransactionHistory = ({ transactions }: TransactionHistoryProps) => {
  const [expandedTx, setExpandedTx] = useState<number | null>(null);
  
  const toggleExpand = (id: number) => {
    setExpandedTx(expandedTx === id ? null : id);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case "pending":
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <XCircle className="w-5 h-5 text-red-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "Transaction completed and verified on the blockchain";
      case "pending":
        return "Transaction is being processed and awaiting confirmation";
      default:
        return "Transaction failed. Please try again or contact support";
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <Card className="border-blue-100/60 shadow-md hover:shadow-lg transition-all">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-t-lg">
        <CardTitle className="text-blue-700">Transaction History</CardTitle>
        <CardDescription>Recent deposits and withdrawals</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          {transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No transactions yet. Make your first deposit to get started.
            </div>
          ) : (
            transactions.map((tx) => (
              <div
                key={tx.id}
                className={cn(
                  "rounded-lg transition-all duration-200 overflow-hidden",
                  expandedTx === tx.id ? "bg-blue-50" : "bg-gray-50 hover:bg-gray-100"
                )}
              >
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => toggleExpand(tx.id)}
                >
                  <div className="flex items-center gap-4">
                    {tx.type === "deposit" ? (
                      <div className="p-2 bg-emerald-100 rounded-full">
                        <ArrowUpCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                    ) : (
                      <div className="p-2 bg-blue-100 rounded-full">
                        <ArrowDownCircle className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                    <div>
                      <div className="font-medium capitalize">{tx.type}</div>
                      <div className="text-sm text-gray-500">{formatDate(tx.date)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-medium">{tx.amount}</div>
                      <div className="text-sm text-gray-500 font-mono">
                        {tx.hash.substring(0, 6)}...{tx.hash.substring(tx.hash.length - 4)}
                      </div>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(tx.status)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{getStatusText(tx.status)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                
                {expandedTx === tx.id && (
                  <div className="px-4 pb-4 text-sm border-t border-blue-100 pt-2 bg-white/50">
                    <div className="flex flex-col space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Transaction Hash:</span>
                        <div className="flex items-center">
                          <span className="font-mono">{tx.hash}</span>
                          <ExternalLink className="w-4 h-4 ml-2 text-blue-500 cursor-pointer" />
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Status:</span>
                        <span className={cn(
                          tx.status === "completed" ? "text-emerald-600" : 
                          tx.status === "pending" ? "text-yellow-600" : "text-red-600"
                        )}>
                          {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                        </span>
                      </div>
                      {tx.verified && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Security:</span>
                          <span className="flex items-center text-emerald-600">
                            <ShieldCheck className="w-4 h-4 mr-1" />
                            Verified
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
